import type React from 'react';
import type { FeatureDepartment } from '../../../types/feature';
import {
  GridIcon, UserCircleIcon, SettingsIcon, DatabaseIcon, BoxIcon, FolderIcon,
  TaskIcon, ListIcon, PieChartIcon, FileTextIcon, TableIcon, HomeIcon,
  PlusIcon, SearchIcon, FilterIcon, EyeIcon, PencilIcon, TrashIcon,
  DownloadIcon, UploadIcon, CheckCircleIcon, AlertIcon, InfoIcon,
  BoltIcon, ArrowUpIcon, ArrowDownIcon, ArrowRightIcon, LockIcon,
  EnvelopeIcon, UserIcon, CalenderIcon, TimeIcon, CopyIcon, GroupIcon,
  ShootingStarIcon, DollarLineIcon, DocsIcon, MailIcon, ChatIcon,
  PlugInIcon, PageIcon, BoxCubeIcon, RefreshIcon,
} from '../../../icons';

// ─── All available icons with emoji fallback ──────────────────────────────────

export type IconEntry = { key: string; label: string; emoji: string; Component: React.ComponentType<React.SVGProps<SVGSVGElement>> };

export const ALL_ICONS: IconEntry[] = [
  { key: 'GridIcon',        label: 'Dashboard',        emoji: '⊞', Component: GridIcon },
  { key: 'UserCircleIcon',  label: 'User',              emoji: '👤', Component: UserCircleIcon },
  { key: 'UserIcon',        label: 'Person',            emoji: '🧑', Component: UserIcon },
  { key: 'SettingsIcon',    label: 'Settings',          emoji: '⚙️', Component: SettingsIcon },
  { key: 'DatabaseIcon',    label: 'Database / Masters',emoji: '🗄️', Component: DatabaseIcon },
  { key: 'BoxIcon',         label: 'Assets / Box',      emoji: '📦', Component: BoxIcon },
  { key: 'BoxCubeIcon',     label: 'Cube',              emoji: '🧊', Component: BoxCubeIcon },
  { key: 'FolderIcon',      label: 'Projects / Folder', emoji: '📁', Component: FolderIcon },
  { key: 'TaskIcon',        label: 'Tasks',             emoji: '✅', Component: TaskIcon },
  { key: 'ListIcon',        label: 'List',              emoji: '📋', Component: ListIcon },
  { key: 'PieChartIcon',    label: 'Reports / Charts',  emoji: '📊', Component: PieChartIcon },
  { key: 'FileTextIcon',    label: 'Documents',         emoji: '📄', Component: FileTextIcon },
  { key: 'TableIcon',       label: 'Tables',            emoji: '🗃️', Component: TableIcon },
  { key: 'HomeIcon',        label: 'Home',              emoji: '🏠', Component: HomeIcon },
  { key: 'PlusIcon',        label: 'Add / Create',      emoji: '➕', Component: PlusIcon },
  { key: 'SearchIcon',      label: 'Search',            emoji: '🔍', Component: SearchIcon },
  { key: 'FilterIcon',      label: 'Filter',            emoji: '🔽', Component: FilterIcon },
  { key: 'EyeIcon',         label: 'View / Eye',        emoji: '👁️', Component: EyeIcon },
  { key: 'PencilIcon',      label: 'Edit',              emoji: '✏️', Component: PencilIcon },
  { key: 'TrashIcon',       label: 'Delete / Trash',    emoji: '🗑️', Component: TrashIcon },
  { key: 'UploadIcon',      label: 'Upload',            emoji: '⬆️', Component: UploadIcon },
  { key: 'DownloadIcon',    label: 'Download',          emoji: '⬇️', Component: DownloadIcon },
  { key: 'CheckCircleIcon', label: 'Success / Check',   emoji: '✅', Component: CheckCircleIcon },
  { key: 'AlertIcon',       label: 'Alert / Warning',   emoji: '⚠️', Component: AlertIcon },
  { key: 'InfoIcon',        label: 'Info',              emoji: 'ℹ️', Component: InfoIcon },
  { key: 'BoltIcon',        label: 'Power / Bolt',      emoji: '⚡', Component: BoltIcon },
  { key: 'ArrowUpIcon',     label: 'Arrow Up',          emoji: '⬆️', Component: ArrowUpIcon },
  { key: 'ArrowDownIcon',   label: 'Arrow Down',        emoji: '⬇️', Component: ArrowDownIcon },
  { key: 'ArrowRightIcon',  label: 'Arrow Right',       emoji: '➡️', Component: ArrowRightIcon },
  { key: 'LockIcon',        label: 'Security / Lock',   emoji: '🔒', Component: LockIcon },
  { key: 'EnvelopeIcon',    label: 'Email',             emoji: '✉️', Component: EnvelopeIcon },
  { key: 'CalenderIcon',    label: 'Calendar',          emoji: '📅', Component: CalenderIcon },
  { key: 'TimeIcon',        label: 'Time / Clock',      emoji: '🕐', Component: TimeIcon },
  { key: 'CopyIcon',        label: 'Copy',              emoji: '📋', Component: CopyIcon },
  { key: 'GroupIcon',       label: 'Group / Team',      emoji: '👥', Component: GroupIcon },
  { key: 'ShootingStarIcon',label: 'Star / Featured',   emoji: '⭐', Component: ShootingStarIcon },
  { key: 'DollarLineIcon',  label: 'Finance / Money',   emoji: '💰', Component: DollarLineIcon },
  { key: 'DocsIcon',        label: 'Documentation',     emoji: '📖', Component: DocsIcon },
  { key: 'MailIcon',        label: 'Mail',              emoji: '📧', Component: MailIcon },
  { key: 'ChatIcon',        label: 'Chat / Messages',   emoji: '💬', Component: ChatIcon },
  { key: 'PlugInIcon',      label: 'Plugin / Connect',  emoji: '🔌', Component: PlugInIcon },
  { key: 'PageIcon',        label: 'Page',              emoji: '📑', Component: PageIcon },
  { key: 'RefreshIcon',     label: 'Refresh / Sync',    emoji: '🔄', Component: RefreshIcon },
];

// ─── Permission action display metadata ──────────────────────────────────────
//
// Live action list now comes from `usePermissionsConfig()` (see spec §4.10).
// Backend `actionCatalog` provides {key, name, description}; this static
// dictionary supplies UI-only chrome (emoji + chip colours) keyed off the
// action key. Unknown action keys fall back to neutral grey.

export type ActionDisplay = { emoji: string; color: string };

export const ACTION_DISPLAY: Record<string, ActionDisplay> = {
  read:       { emoji: '👁️', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  create:     { emoji: '➕', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  update:     { emoji: '✏️', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  delete:     { emoji: '🗑️', color: 'bg-red-100 text-red-700 border-red-200' },
  activate:   { emoji: '✅', color: 'bg-green-100 text-green-700 border-green-200' },
  deactivate: { emoji: '⛔', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  generate:   { emoji: '⚡', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  manage:     { emoji: '⚙️', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  approve:    { emoji: '✔️', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  reject:     { emoji: '✖️', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  import:     { emoji: '📥', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  export:     { emoji: '📤', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
};

export const DEFAULT_ACTION_DISPLAY: ActionDisplay = { emoji: '🔘', color: 'bg-gray-100 text-gray-700 border-gray-200' };

export function getActionDisplay(key: string): ActionDisplay {
  return ACTION_DISPLAY[key] ?? DEFAULT_ACTION_DISPLAY;
}

// ─── Business Unit config ──────────────────────────────────────────────────────

export const DEPT_OPTIONS: { value: FeatureDepartment; label: string; shortLabel: string; color: string; icon: string }[] = [
  { value: 'COMMON', label: 'Common (All Business Units)', shortLabel: 'COMMON', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: '🌐' },
  { value: 'OMS',    label: 'OMS — Operations Management', shortLabel: 'OMS', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: '⚙️' },
  { value: 'AMS',    label: 'AMS — Asset Management',      shortLabel: 'AMS', color: 'bg-purple-100 text-purple-700 border-purple-300',  icon: '🏗️' },
  { value: 'EPC',    label: 'EPC — Engg. Procurement & Construction', shortLabel: 'EPC', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: '🔧' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function toKebabCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function toCamelCase(str: string): string {
  return str
    .replace(/[-\s]+(.)/g, (_, c) => c.toUpperCase())
    .replace(/^(.)/, (_, c) => c.toLowerCase())
    .replace(/[^a-zA-Z0-9]/g, '');
}

// ─── Permission CRUD Scope Component ─────────────────────────────────────────

export interface ActionCatalogEntry {
  key: string;
  name: string;
  description: string;
}
